import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import classnames from 'classnames';

import {CopyIconButton} from 'shells/button/copy-button/copy-icon-button';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import styles from './phone-status-button.module.css';

export function getStatusText(
    isEnrolled: boolean,
    phoneNumber: string | null | undefined,
    isUnderReview: boolean
): string {
    if (isUnderReview) {
        return 'Under review';
    } else if (!isEnrolled || !phoneNumber) {
        return 'SMS not configured';
    } else {
        return `Configured: ${phoneNumber}`;
    }
}

export function getStatusDescription(
    isEnrolled: boolean,
    phoneNumber: string | null | undefined,
    isUnderReview: boolean
): React.ReactElement<any> {
    if (isUnderReview) {
        return <div>SMS enrollment is under review</div>;
    } else if (!isEnrolled || !phoneNumber) {
        return <div>SMS hasn’t been configured</div>;
    } else {
        return (
            <StackLegacy spacing={6} isHorizontal={true} alignCenter={true}>
                <Body>SMS configured:</Body>
                <Body>{phoneNumber}</Body>
                <CopyIconButton text={phoneNumber} as='div' popoverBodyColor='light' />
            </StackLegacy>
        );
    }
}

const getRemainingCreditsContent = (
    remainingCredits: number,
    userCanAccessSetup: boolean,
    isEmbedded?: boolean
) => {
    const noCreditsLinkToBilling = userCanAccessSetup && (
        <StackLegacy spacing={8}>
            <Body size='small'>Consider upgrading your plan to ensure uninterrupted service.</Body>
            <div className='flex justify-center'>
                {/* @ts-expect-error */}
                <Link as={RoutedLink} variant='button-upgrade' to='/setup/billing/sales' size='big'>
                    Upgrade plan
                </Link>
            </div>
        </StackLegacy>
    );

    const lowCreditsLinkToBilling = userCanAccessSetup && (
        <Body size='small'>
            Consider {/* @ts-expect-error */}
            <Link as={RoutedLink} size='small' variant='primary' to='/setup/billing/sales'>
                upgrading your plan
            </Link>{' '}
            to ensure uninterrupted service.
        </Body>
    );

    const standardLinkToBilling = userCanAccessSetup && (
        <Body size='small'>
            If you’d like to add more, you can {/* @ts-expect-error */}
            <Link as={RoutedLink} variant='primary' size='small' to='/setup/billing/sales'>
                upgrade your plan
            </Link>{' '}
            at any time.
        </Body>
    );

    if (remainingCredits < 1) {
        return (
            <StackLegacy spacing={16}>
                <Body size='small'>
                    You have{' '}
                    <Body size='small' isBold={true}>
                        0
                    </Body>{' '}
                    text messages remaining for the month. {!isEmbedded && noCreditsLinkToBilling}
                </Body>
            </StackLegacy>
        );
    } else if (remainingCredits < 11) {
        return (
            <Body size='small'>
                You have{' '}
                <Body size='small' isBold={true}>
                    {remainingCredits}
                </Body>{' '}
                text messages remaining for the month. {!isEmbedded && lowCreditsLinkToBilling}
            </Body>
        );
    } else {
        return (
            <Body size='small'>
                You have{' '}
                <Body size='small' isBold={true}>
                    {remainingCredits}
                </Body>{' '}
                text messages remaining for the month. {!isEmbedded && standardLinkToBilling}
            </Body>
        );
    }
};

export function getPopoverContent(input: {
    isEnrolled: boolean;
    phoneNumber: string | null | undefined;
    remainingCredits: number;
    isBeta?: boolean;
    userCanAccessSetup: boolean;
    isUnderReview?: boolean;
    isEmbedded?: boolean;
}): React.ReactElement<any> {
    if (input.isUnderReview) {
        return (
            <div className={styles['popover-container']}>
                {input.userCanAccessSetup ? (
                    <StackLegacy spacing={16}>
                        <Body>
                            Nice! You’ve submitted your form. Your enrollment is currently under
                            review. You’ll be able to send texts once it’s complete.
                        </Body>
                        <Body>
                            You can track your progress anytime in {/* @ts-expect-error */}
                            <Link as={RoutedLink} to='/inbox/settings/sms' variant='primary'>
                                SMS settings
                            </Link>
                            .
                        </Body>
                    </StackLegacy>
                ) : (
                    <Body>
                        Nice! Your admin has submitted your form. Your enrollment is currently under
                        review. You’ll be able to send texts once it’s complete.
                    </Body>
                )}
            </div>
        );
    } else if (!input.isEnrolled || !input.phoneNumber) {
        return (
            <div className={styles['popover-container']}>
                <StackLegacy spacing={16}>
                    <Body>
                        To send outbound text messages from Nutshell, you must enroll your business
                        and configure a phone number.
                    </Body>
                    <div className={styles['action-container']}>
                        {input.userCanAccessSetup ? (
                            <Link
                                // @ts-expect-error
                                as={RoutedLink}
                                variant='button-primary'
                                size='big'
                                to='/inbox/settings/sms'
                            >
                                Get started
                            </Link>
                        ) : (
                            <Body as='div' size='small'>
                                To get started, please reach out to an admin in your organization
                                who can enroll your business and setup a phone number
                            </Body>
                        )}
                        <Link
                            variant='primary'
                            size='small'
                            newTab={true}
                            href='https://support.nutshell.com/en/articles/9674420-nutshell-sms-how-to-send-and-manage-texts'
                        >
                            Learn more about Nutshell SMS
                        </Link>
                    </div>
                </StackLegacy>
            </div>
        );
    } else if (input.isBeta) {
        return (
            <div className={classnames('text-align-left', styles['popover-container'])}>
                <Body size='normal' as='div' isBold={true}>
                    Thank you for being an early beta tester for SMS!{' '}
                </Body>
                <div className='mb-8' />
                <Body size='normal' as='div'>
                    If you have any questions, feedback, or run into any issues, please don’t
                    hesitate to contact our support team.
                </Body>
            </div>
        );
    } else {
        return (
            <div className={styles['popover-container']}>
                {getRemainingCreditsContent(
                    input.remainingCredits,
                    input.userCanAccessSetup,
                    input.isEmbedded
                )}
            </div>
        );
    }
}
